import React, { createContext, useContext, useState, ReactNode } from 'react';

interface BlockedUsers {
  [userId: string]: boolean;
}

interface BlockUnblockContextType {
  blockedUsers: BlockedUsers;
  toggleBlock: (userId: string) => void;
  isUserBlocked: (userId: string) => boolean;
}

/**
 * Creates the BlockUnblockContext with undefined as default value
 * WHY UNDEFINED:
 * - Forces components to use the context within a Provider
 * - Prevents accidental use outside of Provider (throws error in hook)
 * - Makes it clear when context is not properly set up
 */
const BlockUnblockContext = createContext<BlockUnblockContextType | undefined>(undefined);

/**
 * Wraps the app to provide block/unblock functionality to all children
 * EXECUTION FLOW:
 * 1. Component mounts → initializes blockedUsers state as {}
 * 2. Defines toggleBlock function to flip block status
 * 3. Defines isUserBlocked function to check block status
 * 4. Wraps children in Context.Provider with value containing state and functions
 * 5. Any child component can now access blockedUsers, toggleBlock, isUserBlocked
 */
export const BlockUnblockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  /**
   * Tracks which users are blocked by the current user
   * INITIAL VALUE: {} (empty object - no users blocked)
   * UPDATES:
   * - When toggleBlock is called → adds/updates userId with flipped boolean value
   * - Does NOT remove entries when unblocking (sets to false instead)
   */
  const [blockedUsers, setBlockedUsers] = useState<BlockedUsers>({});
  
  /**
   * Toggles the blocked status of a user (block ↔ unblock)
   * PARAMETERS:
   * - userId: The unique ID of the user to block/unblock
   * BEHAVIOR:
   * - If user is blocked (true) → sets to unblocked (false)
   * - If user is unblocked (false) → sets to blocked (true)
   * - If user is not in blockedUsers yet → sets to true (blocked)
   * IMPLEMENTATION:
   * Uses functional state update to ensure correct state when called multiple times
   * Spreads previous state (...prev) to preserve other users' block status
   * Uses computed property [userId] to update specific user
   * Flips boolean with !prev[userId] (undefined becomes true)
   */
  const toggleBlock = (userId: string) => {
    setBlockedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  /**
   * Checks if a specific user is currently blocked
   * PARAMETERS:
   * - userId: The unique ID of the user to check
   * RETURNS:
   * - true if user is blocked
   * - false if user is not blocked or not in blockedUsers map
   * BEHAVIOR:
   * Returns blockedUsers[userId] if it exists, otherwise returns false
   * This handles three cases:
   * 1. User is explicitly blocked → returns true
   * 2. User is explicitly unblocked → returns false
   * 3. User is not in map yet → returns false (default to not blocked)
   */
  const isUserBlocked = (userId: string) => {
    return blockedUsers[userId] || false;
  };
  
  /**
   * Wraps children with BlockUnblockContext.Provider
   * VALUE PROVIDED:
   * - blockedUsers: Current state of all blocked users
   * - toggleBlock: Function to block/unblock a user
   * - isUserBlocked: Function to check if a user is blocked
   * EFFECT:
   * All children (and their children) can now access this value via useBlockUnblockContext()
   */
  return (
    <BlockUnblockContext.Provider value={{ blockedUsers, toggleBlock, isUserBlocked }}>
      {children}
    </BlockUnblockContext.Provider>
  );
};

/**
 * CUSTOM HOOK: useBlockUnblockContext, Provides easy access to block/unblock context in any component
 * RETURNS:
 * BlockUnblockContextType object with:
 * - blockedUsers: Map of blocked users
 * - toggleBlock: Function to block/unblock
 * - isUserBlocked: Function to check block status 
 * ERROR HANDLING:
 * Throws an error if used outside of BlockUnblockProvider
 * This prevents bugs from missing provider in component tree
 * TYPICAL CONSUMERS:
 * - MessagingChannelHeader (block/unblock button)
 * - MessagingChannelPreview (show/hide blocked channels)
 * - ChannelInner (filter messages from blocked users)
 */
export const useBlockUnblockContext = () => {
  const context = useContext(BlockUnblockContext);
  if (!context) {
    throw new Error('useBlockUnblockContext must be used within a BlockUnblockProvider');
  }
  return context;
};
